import { useState } from "react";
import type { NBAItem } from "../types";
import type { NbaFilter } from "../../../skills/registry";
import { ApprovalsPanel } from "../../../approvals";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { ActionItem } from "./ActionItem";
import shared from "../../../shared/components/shared.module.css";
import styles from "./ActionsTabPanel.module.css";

/** Sub-tabs shown inside the Actions tab. "actions" is the NBA list;
 *  "approvals" nests the background-skill HITL panel that used to be a
 *  top-level tab. The Word cross-reference/defined-terms findings list
 *  (AIVA-7506) used to nest here too as "docreview" — it now renders
 *  inline in the Chat tab instead, so there is no sub-tab for it. */
export type ActionsSubTab = "actions" | "approvals";

const SUB_TABS: ReadonlyArray<{ readonly id: ActionsSubTab; readonly label: string }> = [
  { id: "actions", label: "Actions" },
  { id: "approvals", label: "Approvals" },
];

interface ActionsTabPanelProps {
  readonly nbaItems: NBAItem[];
  readonly nbaLoading: boolean;
  readonly nbaError: string | null;
  readonly nbaEmailsAnalysed: number;
  readonly onToggleNbaStatus: (nbaId: string | null) => void;
  readonly onExecuteAction?: (action: NBAItem, responseType?: string) => void;
  readonly onClearAll?: () => void;
  /** Active sub-tab (controlled so notification deep-links can open Approvals). */
  readonly subTab: ActionsSubTab;
  readonly onSubTabChange: (next: ActionsSubTab) => void;
  /** Forwarded to the nested Approvals panel. */
  readonly apiToken: string | null;
  readonly refreshTokens?: RefreshTokenFn;
}

export function ActionsTabPanel({
  nbaItems,
  nbaLoading,
  nbaError,
  nbaEmailsAnalysed,
  onToggleNbaStatus,
  onExecuteAction,
  onClearAll,
  subTab,
  onSubTabChange,
  apiToken,
  refreshTokens,
}: Readonly<ActionsTabPanelProps>) {
  const [filter, setFilter] = useState<NbaFilter>("outstanding");

  const sorted = [...nbaItems].sort((a, b) => {
    const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const pa = priorityOrder[a.priority] ?? 3;
    const pb = priorityOrder[b.priority] ?? 3;
    if (pa !== pb) return pa - pb;
    return b.confidence - a.confidence;
  });

  const filtered = sorted.filter((item) => {
    if (filter === "outstanding") return !item.is_completed;
    if (filter === "completed") return item.is_completed;
    return true;
  });

  const outstandingCount = nbaItems.filter((i) => !i.is_completed).length;
  const completedCount = nbaItems.filter((i) => i.is_completed).length;
  const overdueCount = 0;

  const subTabBar = (
    <div className={styles.subTabBar} role="tablist" aria-label="Actions views">
      {SUB_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={subTab === tab.id}
          className={`${styles.subTab} ${subTab === tab.id ? styles.subTabActive : ""}`}
          onClick={() => onSubTabChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (subTab === "approvals") {
    return (
      <div className={styles.actionsPanel}>
        {subTabBar}
        <ApprovalsPanel apiToken={apiToken} refreshTokens={refreshTokens} />
      </div>
    );
  }

  return (
    <div className={styles.actionsPanel}>
      {subTabBar}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{outstandingCount}</div>
          <div className={styles.statLabel}>Outstanding</div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statValue} ${styles.statValueSuccess}`}>{completedCount}</div>
          <div className={styles.statLabel}>Completed</div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statValue} ${styles.statValueDanger}`}>{overdueCount}</div>
          <div className={styles.statLabel}>Overdue</div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterButtons}>
          <button
            type="button"
            className={`${styles.chip} ${filter === "outstanding" ? styles.chipActive : ""}`}
            onClick={() => setFilter("outstanding")}
          >
            Outstanding
          </button>
          <button
            type="button"
            className={`${styles.chip} ${filter === "completed" ? styles.chipActive : ""}`}
            onClick={() => setFilter("completed")}
          >
            Completed
          </button>
          <button
            type="button"
            className={`${styles.chip} ${filter === "all" ? styles.chipActive : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
        </div>
        <button
          type="button"
          className={styles.clearAllBtn}
          onClick={onClearAll}
          disabled={!onClearAll || nbaItems.length === 0}
        >
          Clear all
        </button>
      </div>

      {nbaLoading && (
        <div className={styles.loading}>
          <span className={shared.dots}>
            <span className={shared.dot} />
            <span className={shared.dot} />
            <span className={shared.dot} />
          </span>
          <span>
            {nbaEmailsAnalysed > 0
              ? `Analysing ${nbaEmailsAnalysed} emails…`
              : "Analysing your unread emails…"}
          </span>
        </div>
      )}

      {nbaError && !nbaLoading && (
        <div className={styles.error}>{nbaError}</div>
      )}

      {!nbaLoading && !nbaError && filtered.length === 0 && (
        <div className={styles.empty}>No actions to show.</div>
      )}

      {filtered.map((action) => (
        <ActionItem
          key={action.nba_id ?? `${action.title}-${action.email_id}`}
          action={action}
          onToggle={() => onToggleNbaStatus(action.nba_id)}
          onExecuteAction={onExecuteAction}
        />
      ))}
    </div>
  );
}
